import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  LogOut, Crown, Sparkles, Globe, Target, Layers, 
  BookOpen, Shield, User, Settings, ChevronDown
} from 'lucide-react';

const AppHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { user, logout, refreshUser } = useAuth();
  const hasRefreshedRef = React.useRef(false);
  
  // Determine active module from URL
  const isRevisionModule = location.pathname.startsWith('/revision') || location.pathname.startsWith('/compare');
  const [activeModule, setActiveModule] = useState(isRevisionModule ? 'revision' : 'balloon');
  
  // Save last module to localStorage
  useEffect(() => {
    localStorage.setItem('smartballoon_last_module', activeModule);
  }, [activeModule]);
  
  // Refresh user data ONCE on mount to get updated plan (not in a loop!)
  useEffect(() => {
    if (!hasRefreshedRef.current && refreshUser) {
      hasRefreshedRef.current = true;
      refreshUser();
    }
  }, []); // Empty deps - only on mount
  
  // Update active module when location changes
  useEffect(() => {
    if (location.pathname.startsWith('/revision') || location.pathname.startsWith('/compare')) {
      setActiveModule('revision');
    } else if (location.pathname.startsWith('/dashboard') || location.pathname.startsWith('/workspace')) {
      setActiveModule('balloon');
    }
  }, [location.pathname]);
  
  const handleModuleSwitch = (module) => {
    setActiveModule(module);
    if (module === 'balloon') {
      navigate('/dashboard');
    } else {
      navigate('/revision');
    }
  };
  
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };
  
  const plan = user?.plan?.toUpperCase() || 'FREE';
  const isAI = plan === 'AI' || plan === 'AI_UNLIMITED';
  
  const getPlanBadge = () => {
    switch (plan) {
      case 'AI_UNLIMITED':
        return (
          <span className="flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs rounded-full">
            <Crown className="w-3 h-3" />
            AI Unlimited
          </span>
        );
      case 'AI':
        return (
          <span className="flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-xs rounded-full">
            <Sparkles className="w-3 h-3" />
            AI Premium
          </span>
        );
      case 'PRO':
        return (
          <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
            PRO
          </span>
        );
      default:
        return (
          <span className="px-2 py-0.5 bg-slate-200 text-slate-600 text-xs rounded-full">
            Free
          </span>
        );
    }
  };
  
  return (
    <header className="h-14 border-b border-slate-200 bg-white flex items-center px-6 justify-between sticky top-0 z-40">
      {/* Left: Logo + Module Navigation */}
      <div className="flex items-center gap-6">
        {/* Logo */}
        <div 
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => navigate('/dashboard')}
        >
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
            <Target className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-lg text-slate-800">SmartBalloon</span>
        </div>
        
        {/* Module Navigation Switch */}
        <div className="flex items-center bg-slate-100 rounded-lg p-1">
          <button
            onClick={() => handleModuleSwitch('balloon')}
            className={`
              flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-all
              ${activeModule === 'balloon' 
                ? 'bg-white text-blue-600 shadow-sm' 
                : 'text-slate-600 hover:text-slate-900'
              }
            `}
            data-testid="nav-smartballoon"
          >
            <Target className="w-4 h-4" />
            SmartBalloon
          </button>
          <button
            onClick={() => handleModuleSwitch('revision')}
            className={`
              flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-all
              ${activeModule === 'revision' 
                ? 'bg-white text-purple-600 shadow-sm' 
                : 'text-slate-600 hover:text-slate-900'
              }
              ${!isAI ? 'opacity-60' : ''}
            `}
            data-testid="nav-smartrevision"
          >
            <Layers className="w-4 h-4" />
            SmartRevision
            {!isAI && (
              <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">AI</span>
            )}
          </button>
        </div>
      </div>
      
      {/* Right: Actions */}
      <div className="flex items-center gap-3">
        {/* Language Selector */}
        <Select 
          value={i18n.language?.substring(0, 2) || 'en'} 
          onValueChange={(val) => {
            i18n.changeLanguage(val);
            localStorage.setItem('language', val);
          }}
        >
          <SelectTrigger className="w-[70px] h-8 text-xs">
            <Globe className="w-3 h-3 mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="en">EN</SelectItem>
            <SelectItem value="it">IT</SelectItem>
            <SelectItem value="de">DE</SelectItem>
            <SelectItem value="fr">FR</SelectItem>
            <SelectItem value="zh">中文</SelectItem>
            <SelectItem value="hi">हिंदी</SelectItem>
            <SelectItem value="ar">عربي</SelectItem>
          </SelectContent>
        </Select>
        
        {/* Guide Link */}
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/guide')}
          className="text-slate-600"
        >
          <BookOpen className="w-4 h-4 mr-1" />
          {t('guide') || 'Guide'}
        </Button>
        
        {/* Plan Badge */}
        <button onClick={() => navigate('/subscription')}>
          {getPlanBadge()}
        </button>
        
        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="flex items-center gap-2">
              <div className="w-7 h-7 bg-slate-200 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-slate-600" />
              </div>
              <span className="text-sm text-slate-700 max-w-[100px] truncate">
                {user?.email?.split('@')[0] || 'User'}
              </span>
              <ChevronDown className="w-3 h-3 text-slate-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => navigate('/subscription')}>
              <Settings className="w-4 h-4 mr-2" />
              Abbonamento
            </DropdownMenuItem>
            {user?.is_admin && (
              <DropdownMenuItem onClick={() => navigate('/admin')}>
                <Shield className="w-4 h-4 mr-2" />
                Admin
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-red-600">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default AppHeader;
